/**
 * Bildirim → site içi bağlantı (saf fonksiyon).
 *
 * Mülk Takibi (type 20, sözleşme v1.3): Notification/GetAllList öğesinde `data` = JSON metni,
 * ör. "{\"type\":\"20\",\"screen\":\"PropertyDetail\",\"pid\":\"…\",\"tab\":\"payments\"}".
 *   pid + tab → /mulk-takibi/panel/mulk/{pid}?tab={tab}  (mülk sayfası İngilizce sekme adlarını da tanır)
 *   data yok / bozuk / pid yok → /mulk-takibi/panel
 *   screen "RentalIncomeTax" + year (beyanname hatırlatması, v2.1) → /mulk-takibi/panel/vergi?yil={year}
 * Kiracı portalı (sözleşme v2.1 §B): data = { type, screen, tid (sözleşme id), pid?, payid?, tab? } (tüm değerler metin)
 *   21 yeni dekont → ev sahibi: /mulk-takibi/panel/mulk/{pid}?tab=payments&dekont={payid}   (pid yoksa panel)
 *   22 dekont kabul/ret → kiracı: /kiraci/{tid}#odemeler                     (tid yoksa /kiraci)
 *   23 yeni mesaj → karşı taraf: kiracıya (screen "Tenant…" ya da pid yok) /kiraci/{tid}#mesajlar,
 *      ev sahibine /mulk-takibi/panel/mulk/{pid}?tab=messages#mesajlar
 * Kayıtlı aramalar (type 24, günlük özet): data = { type:"24", screen:"SavedSearch", sid } (PID = sid)
 *   → /kayitli-aramalar?sid={sid}  (sid yok/güvensiz → /kayitli-aramalar)
 * Diğer bildirim türleri için null (tıklanabilir değil; mevcut davranış).
 */

export const PROPERTY_TRACKING_NOTIFICATION_TYPE = 20;
export const TENANT_NOTIFICATION_TYPES = Object.freeze({ RECEIPT_NEW: 21, RECEIPT_REVIEWED: 22, MESSAGE: 23 });
export const SAVED_SEARCH_NOTIFICATION_TYPE = 24;
const PANEL = '/mulk-takibi/panel';
const TENANT_HOME = '/kiraci';
const SAVED_SEARCHES = '/kayitli-aramalar';
const HANDLED = [20, 21, 22, 23, 24];

/** data alanı: JSON metni ya da nesne → nesne | null */
export const parseNotificationData = (data) => {
  if (data == null || data === '') return null;
  if (typeof data === 'object') return Array.isArray(data) ? null : data;
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Bildirim türü: kayıt alanı (type / notificationType) ya da data.type; işlenmeyen türlerde null */
const typeOf = (n, data) => {
  for (const v of [n?.type, n?.notificationType, data?.type]) {
    const t = Number(v);
    if (HANDLED.includes(t)) return t;
  }
  return null;
};

// Kimlik ve sekme yalnızca güvenli karakterlerden oluşabilir (yol enjeksiyonu yok)
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const SAFE_TAB = /^[A-Za-z]{1,32}$/;
const safeId = (v) => (v != null && SAFE_ID.test(String(v)) ? String(v) : '');
const safeTab = (v) => (typeof v === 'string' && SAFE_TAB.test(v) ? v : '');

const propertyLink = (pid, tab, hash, extra) => {
  const qs = new URLSearchParams();
  if (tab) qs.set('tab', tab);
  Object.entries(extra || {}).forEach(([k, v]) => v && qs.set(k, v));
  const q = qs.toString();
  return `${PANEL}/mulk/${encodeURIComponent(pid)}${q ? `?${q}` : ''}${hash ? `#${hash}` : ''}`;
};
const SAFE_YEAR = /^(19|20)\d{2}$/;
const tenantLink = (tid, hash) => (tid ? `${TENANT_HOME}/${encodeURIComponent(tid)}${hash ? `#${hash}` : ''}` : TENANT_HOME);

/** Mesaj bildirimi kiracıya mı (screen "TenantPortal"/"TenantTenancy" vb. ya da yalnızca tid varsa) */
const isTenantSide = (data) => {
  const screen = String(data?.screen || '');
  if (/tenant|kiraci/i.test(screen)) return true;
  if (/property|owner|mulk/i.test(screen)) return false;
  return !safeId(data?.pid) && Boolean(safeId(data?.tid));
};

/** Bildirim → yol (string) ya da null */
export const notificationLink = (n) => {
  if (!n || typeof n !== 'object') return null;
  const data = parseNotificationData(n.data);
  const type = typeOf(n, data);
  if (type == null) return null;
  const pid = safeId(data?.pid);
  const tid = safeId(data?.tid);
  const payid = safeId(data?.payid);
  const tab = safeTab(data?.tab);
  switch (type) {
    case SAVED_SEARCH_NOTIFICATION_TYPE: {
      const sid = safeId(data?.sid) || safeId(n.pid);
      return sid && sid !== '00000000-0000-0000-0000-000000000000'
        ? `${SAVED_SEARCHES}?sid=${encodeURIComponent(sid)}`
        : SAVED_SEARCHES;
    }
    case TENANT_NOTIFICATION_TYPES.RECEIPT_NEW:
      // payid → Ödemeler sekmesi o satırın dekontlarını açar (?dekont=)
      return pid ? propertyLink(pid, tab || 'payments', '', { dekont: payid }) : PANEL;
    case TENANT_NOTIFICATION_TYPES.RECEIPT_REVIEWED:
      return tenantLink(tid, tid ? 'odemeler' : '');
    case TENANT_NOTIFICATION_TYPES.MESSAGE:
      if (isTenantSide(data)) return tenantLink(tid, tid ? 'mesajlar' : '');
      return pid ? propertyLink(pid, 'messages', 'mesajlar') : PANEL;
    default:
      // Tür 20 + screen "RentalIncomeTax" (beyanname hatırlatması, v2.1): vergi yardımcısı, gelir yılı `year`
      if (String(data?.screen || '') === 'RentalIncomeTax') {
        const year = String(data?.year ?? '');
        return `${PANEL}/vergi${SAFE_YEAR.test(year) ? `?yil=${year}` : ''}`;
      }
      return pid ? propertyLink(pid, tab) : PANEL;
  }
};

/** Bağlantı etiketi için hedef: 'tenant' (kiracı portalı) | 'savedSearch' (kayıtlı aramalar) | 'owner' (Mülk Takibi) | null */
export const notificationTarget = (n) => {
  const link = notificationLink(n);
  if (!link) return null;
  if (link.startsWith(SAVED_SEARCHES)) return 'savedSearch';
  return link.startsWith(TENANT_HOME) ? 'tenant' : 'owner';
};
